import { useEffect } from 'react';
import { processOfflineQueue } from '@/utils/projectStorage';
import { useUpdateProject } from '@/hooks/apis/useProject';

/**
 * Hook to process offline queue when coming back online
 * This should be used at the app level to handle queued project updates
 */
export function useOfflineQueue() {
  const updateProjectMutation = useUpdateProject({
    meta: { suppressErrorToast: true }, // Suppress toasts for background sync
  });

  useEffect(() => {
    const handleOnline = async () => {
      if (navigator.onLine) {
        try {
          await processOfflineQueue(async (variables) => {
            await updateProjectMutation.mutateAsync(variables);
          });
        } catch (error) {
          if (import.meta.env.DEV) {
            console.warn('[OfflineQueue] Failed to process some queued updates:', error);
          }
        }
      }
    };

    // Process queue immediately if already online
    if (navigator.onLine) {
      handleOnline();
    }

    // Listen for online event
    window.addEventListener('online', handleOnline);

    // Also check periodically (every 30 seconds) in case online event doesn't fire
    const intervalId = setInterval(() => {
      if (navigator.onLine) {
        handleOnline();
      }
    }, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      clearInterval(intervalId);
    };
  }, [updateProjectMutation]);
}

